import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import type { Provider, ResolutionMode } from "@/api.types.v2";

const API_URL = import.meta.env.VITE_API_URL;

// Carrier Alias types
export interface CarrierAlias {
  id: string;
  provider: Provider;
  alias: string;
  canonical: string;
  createdAt: string;
  updatedAt: string;
}

// Resolution Rule types
export interface CarrierResolutionRule {
  id: string;
  carrierCode: string;
  serviceCode?: string;
  mode: ResolutionMode;
  preferer?: Provider;
  note?: string;
  createdAt: string;
  updatedAt: string;
}

// ========================= Carrier Aliases =========================

export function useGetCarrierAliases(params?: {
  provider?: Provider;
  page?: number;
  limit?: number;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["carrier-aliases", params],
    queryFn: async () => {
      const { data } = await axios.get<{
        data: CarrierAlias[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }>(`${API_URL}/api/v2/carrier-aliases`, {
        params: {
          carrierId: params?.provider,
          page: params?.page ?? 1,
          limit: params?.limit ?? 50,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data;
    },
    enabled: !!token,
  });
}

export function useCreateCarrierAlias(options?: {
  onSuccess?: (data: CarrierAlias) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (alias: {
      provider: Provider;
      alias: string;
      canonical: string;
    }) => {
      // Backend expects carrierId, not provider
      const { data } = await axios.post<CarrierAlias>(
        `${API_URL}/api/v2/carrier-aliases`,
        {
          carrierId: alias.provider,
          canonical: alias.canonical,
          // Note: the 'alias' field is not in the schema, might need to adjust
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

// ========================= Resolution Rules =========================

export function useGetResolutionRules(params?: {
  carrierCode?: string;
  page?: number;
  limit?: number;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["resolution-rules", params],
    queryFn: async () => {
      const { data } = await axios.get<{
        data: CarrierResolutionRule[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }>(`${API_URL}/api/v2/carrier-resolution-rules`, {
        params: {
          carrierCode: params?.carrierCode,
          page: params?.page ?? 1,
          limit: params?.limit ?? 50,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data;
    },
    enabled: !!token,
  });
}

export function useUpsertResolutionRule(options?: {
  onSuccess?: (data: CarrierResolutionRule) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (rule: {
      carrierCode: string;
      serviceCode?: string;
      mode: ResolutionMode;
      preferer?: Provider;
      note?: string;
    }) => {
      // Backend expects prefererCarrierId, not preferer
      const { data } = await axios.post<CarrierResolutionRule>(
        `${API_URL}/api/v2/carrier-resolution-rules`,
        {
          carrierCode: rule.carrierCode,
          serviceCode: rule.serviceCode,
          mode: rule.mode,
          prefererCarrierId: rule.preferer,
          note: rule.note,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}
